/**
 * Public legal copy for store and OAuth provider requirements (Meta, Apple, etc.).
 * English only — update when product or data practices change.
 */

import { WC2026_ORG_BILLING_TERMS_SECTION } from '../../shared/constants/wc2026OrgBillingDisclosure.constants'
import {
  VIRTUAL_CURRENCY_SECTION_KEY,
  VIRTUAL_CURRENCY_SECTION_TITLE,
  VIRTUAL_CURRENCY_SECTION_PARAGRAPHS,
} from '../../shared/constants/virtualCurrency.constants'

export const LEGAL_SUPPORT_EMAIL = 'info@zporti.com' as const

export const LEGAL_UI_STRINGS = {
  BACK_TO_HOME: 'Back to home',
  BACK_TO_PRIVACY_AND_DATA: 'Back',
  PRIVACY_DOCUMENT_TITLE: 'Privacy Policy',
  DATA_DELETION_DOCUMENT_TITLE: 'Data deletion instructions',
  TERMS_DOCUMENT_TITLE: 'Terms of Service',
  CREDITS_DOCUMENT_TITLE: 'Credits',
} as const

export interface LegalSection {
  readonly title: string
  readonly paragraphs: readonly string[]
  /**
   * Stable identifier, independent of title translation. Lets the content helper detect
   * whether a localized document already contains a given section before falling back to
   * the English text (see legalDocumentContent.helper.ts).
   */
  readonly key?: string
}

/** Shared Terms section so the published Terms and the in-app coin disclosure cannot drift. */
export const VIRTUAL_CURRENCY_SECTION: LegalSection = {
  key: VIRTUAL_CURRENCY_SECTION_KEY,
  title: VIRTUAL_CURRENCY_SECTION_TITLE,
  paragraphs: VIRTUAL_CURRENCY_SECTION_PARAGRAPHS,
}

export const PRIVACY_POLICY_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Introduction',
    paragraphs: [
      'Zporti (“we”, “us”, or “our”) operates the Zporti mobile and web application (the “Service”). This Privacy Policy describes how we collect, use, and share information when you use the Service.',
      'By using the Service, you agree to this policy. If you do not agree, please do not use the Service.',
    ],
  },
  {
    title: 'Information we collect',
    paragraphs: [
      'Account and profile information: such as name, email address, username, avatar or profile image selections, and similar details you provide when you create or update your account.',
      'Authentication data: we use Supabase authentication and may support email sign-in, OAuth providers (for example Google or Facebook), and guest sessions depending on platform and release.',
      'Content you create: such as sports predictions, challenges, group memberships, friend connections, invitations, and messages you send through contact or support forms.',
      'Usage and device information: such as app events, approximate location derived from IP address where applicable, device type, operating system, and diagnostic data to keep the Service secure and reliable.',
    ],
  },
  {
    title: 'Cookies, analytics, and consent',
    paragraphs: [
      'On web, we use essential cookies/storage for authentication, security, and core app operation.',
      'Non-essential analytics are disabled by default until you provide consent. You can change this preference later from in-app Privacy controls.',
      'If enabled, analytics providers may process pseudonymous usage events and limited account metadata to help us improve product quality and reliability.',
    ],
  },
  {
    title: 'Marketing communications',
    paragraphs: [
      'Marketing emails are opt-in. We only send marketing communications when you actively consent.',
      'You can withdraw marketing consent at any time from Privacy controls.',
      'When you create an account, we record the time and policy version for optional marketing consent and for your acceptance of our Terms of Service and Privacy Policy, so we can demonstrate what you agreed to if required.',
    ],
  },
  {
    title: 'How we use information',
    paragraphs: [
      'We use the information above to provide and improve the Service, authenticate users, personalize your experience, operate challenges and leaderboards, send service-related notices, analyze usage in aggregate, prevent fraud and abuse, and comply with law.',
    ],
  },
  {
    title: 'How we share information',
    paragraphs: [
      'We share information with service providers who help us host the Service, send email, process analytics, or perform similar functions, subject to appropriate safeguards.',
      'We may disclose information if required by law, to protect our rights and users, or in connection with a merger or sale of assets (with notice where required).',
      'Other users may see information you choose to make visible in the app (for example display name, avatar, and challenge-related activity) according to product features.',
    ],
  },
  {
    title: 'Retention',
    paragraphs: [
      'We retain information for as long as your account is active and as needed to provide the Service, comply with legal obligations, resolve disputes, and enforce our agreements.',
    ],
  },
  {
    title: 'Your choices and rights',
    paragraphs: [
      'Depending on where you live, you may have rights to access, correct, delete, or export your personal information, or to object to or restrict certain processing.',
      'In-app Privacy controls include account deletion requests, marketing-consent controls, and data export download where available.',
      `You may also contact us at ${LEGAL_SUPPORT_EMAIL}. We may need to verify your request before responding.`,
    ],
  },
  {
    title: 'Children',
    paragraphs: [
      'The Service is for users aged 18 and over and is not directed to children. We do not knowingly collect personal information from anyone under 18, and we delete such information when we become aware of it.',
    ],
  },
  {
    title: 'Changes',
    paragraphs: [
      'We may update this Privacy Policy from time to time. We will post the updated version on this page and update the effective date when we do.',
    ],
  },
  {
    title: 'Contact',
    paragraphs: [
      `Questions about this policy: ${LEGAL_SUPPORT_EMAIL}`,
    ],
  },
] as const

export const DATA_DELETION_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Overview',
    paragraphs: [
      'You can request deletion of personal data associated with your Zporti account. This page describes how to make that request and what to expect.',
    ],
  },
  {
    title: 'How to request deletion',
    paragraphs: [
      'Use in-app Privacy controls to request account deletion from your authenticated session.',
      `Or email ${LEGAL_SUPPORT_EMAIL} from the email address associated with your Zporti account (or clearly identify the account email and any other details we can use to locate your profile).`,
      'Use the subject line “Data deletion request”. We may ask you to confirm your identity before processing the request.',
      'You can also download a copy of your account data from in-app Privacy controls.',
    ],
  },
  {
    title: 'What we delete',
    paragraphs: [
      'After verification, we will delete or anonymize personal data tied to your account where retention is not required for legal, security, or dispute-resolution purposes.',
      'Some information may remain in backups for a limited period until those backups are rotated, or where the law requires retention.',
    ],
  },
  {
    title: 'Timing',
    paragraphs: [
      'We aim to respond to verified requests within a reasonable period, and within any timeframe required by applicable law in your region.',
    ],
  },
  {
    title: 'Contact',
    paragraphs: [
      `Data deletion questions: ${LEGAL_SUPPORT_EMAIL}`,
    ],
  },
] as const

export const TERMS_OF_SERVICE_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Agreement',
    paragraphs: [
      'These Terms of Service (“Terms”) govern your access to and use of the Zporti mobile and web application (the “Service”) operated by Zporti (“we”, “us”, or “our”).',
      'By creating an account, accessing, or using the Service, you agree to these Terms. If you do not agree, do not use the Service.',
      'When you register, you confirm acceptance of these Terms and our Privacy Policy separately from any optional consent to marketing emails.',
    ],
  },
  {
    title: 'The Service',
    paragraphs: [
      'Zporti provides a social sports prediction and challenge experience: you can create or join challenges with friends, submit predictions, view results and leaderboards, and use related features we may add or change over time.',
      'We may modify, suspend, or discontinue parts of the Service with reasonable notice where practicable. We do not guarantee uninterrupted or error-free operation.',
    ],
  },
  VIRTUAL_CURRENCY_SECTION,
  {
    title: 'Eligibility and accounts',
    paragraphs: [
      'The Service is for users aged 18 and over. You must be at least 18 years old to create an account or use the Service, and older where local law requires a higher minimum age. We may suspend or close accounts where we believe the user is under 18.',
      'You are responsible for the accuracy of information you provide and for keeping your login credentials secure. Notify us promptly at the contact below if you suspect unauthorized access to your account.',
    ],
  },
  {
    title: 'Acceptable use',
    paragraphs: [
      'You agree not to misuse the Service. For example, you must not: break the law; harass, abuse, or harm others; attempt to gain unauthorized access to our systems or other users’ accounts; scrape or overload the Service; distribute malware; cheat, manipulate, or disrupt challenges or outcomes; or use the Service to send spam or deceptive communications.',
      'We may investigate and suspend or terminate accounts that violate these Terms or put the Service or users at risk.',
    ],
  },
  {
    title: 'User content',
    paragraphs: [
      'You retain rights to content you submit (such as profile information, messages, and challenge activity). You grant us a non-exclusive license to host, store, display, and process that content as needed to operate and improve the Service.',
      'You are responsible for content you submit and confirm you have the rights to share it. We may remove content that violates these Terms or applicable law.',
    ],
  },
  {
    title: WC2026_ORG_BILLING_TERMS_SECTION.title,
    paragraphs: [...WC2026_ORG_BILLING_TERMS_SECTION.paragraphs],
  },
  {
    title: 'Disclaimers',
    paragraphs: [
      'The Service is provided “as is” and “as available” to the fullest extent permitted by law. Sports data, odds, and results may come from third parties; we do not warrant their accuracy or timeliness.',
      'Friendly competition and predictions are for entertainment. Nothing in the Service constitutes gambling, financial, or professional advice where such activity is regulated or prohibited.',
    ],
  },
  {
    title: 'Limitation of liability',
    paragraphs: [
      'To the fullest extent permitted by applicable law, we are not liable for indirect, incidental, special, consequential, or punitive damages, or for loss of profits, data, or goodwill, arising from your use of the Service.',
      'Our total liability for claims arising from the Service is limited to the greater of (a) the amount you paid us for the Service in the twelve months before the claim or (b) one hundred U.S. dollars (USD $100), except where law does not allow such a cap.',
    ],
  },
  {
    title: 'Indemnity',
    paragraphs: [
      'You agree to defend and indemnify us and our affiliates, officers, and employees against claims, damages, and expenses (including reasonable legal fees) arising from your use of the Service, your content, or your violation of these Terms, to the extent permitted by law.',
    ],
  },
  {
    title: 'Governing law and disputes',
    paragraphs: [
      'These Terms are governed by the laws applicable in the place where Zporti operates its business, without regard to conflict-of-law rules, except where mandatory consumer protections in your country say otherwise.',
      'If you are a consumer, you may have rights to bring disputes in the courts of your place of residence where required by law.',
    ],
  },
  {
    title: 'Changes',
    paragraphs: [
      'We may update these Terms from time to time. We will post the updated Terms on this page. Continued use of the Service after changes become effective constitutes acceptance of the revised Terms where allowed by law.',
    ],
  },
  {
    title: 'Contact',
    paragraphs: [
      `Questions about these Terms: ${LEGAL_SUPPORT_EMAIL}`,
    ],
  },
] as const

export const ABOUT_SECTIONS: readonly LegalSection[] = [
  {
    title: 'What Zporti is',
    paragraphs: [
      'Zporti is a free-to-play social prediction and trivia game. You predict the outcomes of real matches across football, basketball, and Formula 1, challenge friends head to head, play arcade and trivia mini-games, and climb the leaderboards.',
      'Everything in Zporti is played with virtual coins. Coins are earned by playing or bought as an optional in-app purchase, and they have no monetary value: they cannot be cashed out, transferred, or exchanged for money or anything of value. There are no cash prizes.',
    ],
  },
  {
    title: 'Who operates the Service',
    paragraphs: [
      'Zporti is built and operated by Zporti, LTD, a company registered in the United Kingdom.',
      'The app is available on the App Store and Google Play, and on the web at zporti.com.',
    ],
  },
  {
    title: 'How it works',
    paragraphs: [
      'Match fixtures, live scores, and odds come from third-party sports-data providers and are refreshed on a schedule. Predictions are scored against the data captured when a challenge is created, so every participant in a challenge is judged on the same basis.',
      'Mini-games, daily puzzles, and leaderboards run alongside predictions and award coins and experience for play.',
    ],
  },
  {
    title: 'Contact',
    paragraphs: [
      `General and business enquiries: ${LEGAL_SUPPORT_EMAIL}.`,
      'For help with your account, see the Support page.',
    ],
  },
]

export const SUPPORT_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Getting help',
    paragraphs: [
      `Email us at ${LEGAL_SUPPORT_EMAIL} and we will get back to you. Tell us your username and the device and app version you are using — it lets us find the problem far faster.`,
      'You can also reach us through the contact form on zporti.com.',
    ],
  },
  {
    title: 'Account and data',
    paragraphs: [
      'You can review consent settings, request a copy of your data, and request account deletion from Privacy controls inside the app.',
      'Step-by-step deletion instructions are on the Data deletion page. Deleting your account removes your profile, predictions, and coin balance; coin balances have no monetary value and are not refundable.',
    ],
  },
  {
    title: 'Purchases',
    paragraphs: [
      'Coin purchases are handled by the App Store or Google Play on mobile, and by our payment provider on the web. Refund requests for mobile purchases are handled by the store you bought through, under that store\u2019s refund policy.',
      `If coins you bought have not appeared in your balance, contact ${LEGAL_SUPPORT_EMAIL} with your receipt and we will reconcile it.`,
    ],
  },
  {
    title: 'Reporting a problem',
    paragraphs: [
      `To report a bug, a scoring dispute, or another user\u2019s behaviour, email ${LEGAL_SUPPORT_EMAIL} with as much detail as you can, including screenshots where relevant.`,
    ],
  },
]

export const CREDITS_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Third-party trademarks',
    paragraphs: [
      'Zporti is not affiliated with, endorsed by, or sponsored by FIFA.',
      'Team names, flags, and match schedules are used for identification only. All trademarks belong to their respective owners.',
    ],
  },
  {
    title: 'Third-party credits',
    paragraphs: [
      'Zporti uses selected third-party audio assets for in-app sound effects.',
      'Sound assets are sourced from Freesound and are used under CC0 (public domain) licensing.',
    ],
  },
  {
    title: 'Icon attribution',
    paragraphs: [
      'Sport icons are sourced from Flaticon and used under its free licence, which requires attribution.',
      'Basketball icons created by justicon — Flaticon: https://www.flaticon.com/free-icons/basketball',
      'Soccer ball icons created by DinosoftLabs — Flaticon: https://www.flaticon.com/free-icons/soccer-ball',
    ],
  },
  {
    title: 'Audio attribution',
    paragraphs: [
      'Freesound: https://freesound.org/',
      'Creative Commons CC0 1.0: https://creativecommons.org/publicdomain/zero/1.0/',
    ],
  },
] as const
